import { useState } from 'react';
import { api } from '../../../api/client';
import { StatusBadge } from '../../../components/shared/StatusBadge';
import { ExternalLink, Pause, Play, DollarSign, Trash2 } from 'lucide-react';
import { useCan, Can } from '../../../lib/rbac';

interface SiteItem {
  site_item_id: string;
  site_id: string;
  title: string;
  price: number;
  currency_id: string;
  status: string;
  available_quantity: number;
  sold_quantity: number;
  net_proceeds: number | null;
  sale_fee: number | null;
  shipping_cost: number | null;
  permalink: string | null;
}

interface SitesTabProps {
  product: { site_items?: SiteItem[] };
  sellerId: number;
  onRefresh: () => void;
}

const SITE_FLAGS: Record<string, string> = {
  MLA: '\u{1F1E6}\u{1F1F7}', MLB: '\u{1F1E7}\u{1F1F7}', MLM: '\u{1F1F2}\u{1F1FD}',
  MLC: '\u{1F1E8}\u{1F1F1}', MCO: '\u{1F1E8}\u{1F1F4}', MLU: '\u{1F1FA}\u{1F1FE}',
};

const STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  active: 'ok',
  paused: 'warning',
  under_review: 'info',
  closed: 'error',
  deleted: 'error',
};

export default function SitesTab({ product, sellerId, onRefresh }: SitesTabProps) {
  const can = useCan();
  const canMoney = can('global.view_money');
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [editingPrice, setEditingPrice] = useState<string | null>(null);
  const [priceInput, setPriceInput] = useState('');

  const siteItems = product.site_items || [];

  const handleToggleStatus = async (item: SiteItem) => {
    const newStatus = item.status === 'active' ? 'paused' : 'active';
    setLoadingAction(`status-${item.site_item_id}`);
    try {
      await api.put(`/catalog/products/${item.site_item_id}/ml/status?seller_id=${sellerId}`, { status: newStatus });
      onRefresh();
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDeleteSiteItem = async (item: SiteItem) => {
    if (!confirm(`¿ELIMINAR site item ${item.site_item_id} (${item.site_id}) permanentemente? Esta acción NO se puede deshacer.`)) return;
    setLoadingAction(`delete-${item.site_item_id}`);
    try {
      await api.delete(`/catalog/products/${item.site_item_id}/site-item?seller_id=${sellerId}`);
      onRefresh();
    } finally {
      setLoadingAction(null);
    }
  };

  const handlePriceSubmit = async (item: SiteItem) => {
    const val = parseFloat(priceInput);
    if (isNaN(val) || val <= 0) return;
    setLoadingAction(`price-${item.site_item_id}`);
    try {
      await api.put(`/catalog/products/${item.site_item_id}/ml/price?seller_id=${sellerId}`, { price: val });
      setEditingPrice(null);
      setPriceInput('');
      onRefresh();
    } finally {
      setLoadingAction(null);
    }
  };

  const startEditPrice = (item: SiteItem) => {
    setEditingPrice(item.site_item_id);
    // Sin la llave del dinero el net proceeds actual NO se precarga en el campo visible.
    setPriceInput(canMoney && item.net_proceeds != null ? String(item.net_proceeds) : '');
  };

  const cancelEditPrice = () => {
    setEditingPrice(null);
    setPriceInput('');
  };

  if (siteItems.length === 0) {
    return (
      <div className="text-center py-12 text-[var(--color-text-muted)]">
        No hay site items asociados a este producto
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            {['Site', 'Item ID', 'Status', 'Precio Local', 'Net Proceeds', 'Sale Fee', 'Shipping', 'Stock', 'Vendidos', 'Acciones'].map((h) => (
              <th key={h} className="text-left py-2 px-3 text-xs font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {siteItems.map((item) => (
            <tr key={item.site_item_id} className="border-b border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)] transition-colors">
              {/* Site */}
              <td className="py-2.5 px-3 whitespace-nowrap">
                <span className="mr-1">{SITE_FLAGS[item.site_id] || ''}</span>
                <span className="font-medium text-[var(--color-text)]">{item.site_id}</span>
              </td>

              {/* Item ID */}
              <td className="py-2.5 px-3">
                {item.permalink ? (
                  <a
                    href={item.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-xs text-[var(--color-primary)] hover:underline"
                  >
                    {item.site_item_id}
                  </a>
                ) : (
                  <span className="font-mono text-xs text-[var(--color-text-muted)]">{item.site_item_id}</span>
                )}
              </td>

              {/* Status */}
              <td className="py-2.5 px-3">
                <StatusBadge status={STATUS_MAP[item.status] || 'unknown'} label={item.status} size="sm" />
              </td>

              {/* Precio Local */}
              <td className="py-2.5 px-3 whitespace-nowrap text-[var(--color-text)]">
                ${item.price?.toFixed(2) || '0.00'} <span className="text-xs text-[var(--color-text-muted)]">{item.currency_id}</span>
              </td>

              {/* Net Proceeds */}
              <td className="py-2.5 px-3 whitespace-nowrap">
                {editingPrice === item.site_item_id ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={priceInput}
                      onChange={(e) => setPriceInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handlePriceSubmit(item);
                        if (e.key === 'Escape') cancelEditPrice();
                      }}
                      className="w-20 px-1.5 py-0.5 text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)]"
                      autoFocus
                    />
                    <button
                      onClick={() => handlePriceSubmit(item)}
                      disabled={loadingAction === `price-${item.site_item_id}`}
                      className="text-xs px-1.5 py-0.5 rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {loadingAction === `price-${item.site_item_id}` ? '...' : 'OK'}
                    </button>
                    <button
                      onClick={cancelEditPrice}
                      className="text-xs px-1.5 py-0.5 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                    >
                      X
                    </button>
                  </div>
                ) : (
                  <span className="text-[var(--color-text)]">
                    {canMoney && item.net_proceeds != null ? `$${item.net_proceeds.toFixed(2)}` : '—'}
                    {canMoney && <span className="text-xs text-[var(--color-text-muted)] ml-0.5">USD</span>}
                  </span>
                )}
              </td>

              {/* Sale Fee */}
              <td className="py-2.5 px-3 whitespace-nowrap text-[var(--color-text-muted)]">
                {canMoney && item.sale_fee != null ? `$${item.sale_fee.toFixed(2)}` : '—'}
              </td>

              {/* Shipping */}
              <td className="py-2.5 px-3 whitespace-nowrap text-[var(--color-text-muted)]">
                {canMoney && item.shipping_cost != null ? `$${item.shipping_cost.toFixed(2)}` : '—'}
              </td>

              {/* Stock */}
              <td className="py-2.5 px-3 text-[var(--color-text)]">
                {item.available_quantity ?? 0}
              </td>

              {/* Vendidos */}
              <td className="py-2.5 px-3 text-[var(--color-text)]">
                {item.sold_quantity ?? 0}
              </td>

              {/* Acciones */}
              <td className="py-2.5 px-3">
                <div className="flex items-center gap-1">
                  {/* Pause/Activate */}
                  <Can cap="catalog.pause_activate">
                    <button
                      onClick={() => handleToggleStatus(item)}
                      disabled={loadingAction === `status-${item.site_item_id}` || item.status === 'closed'}
                      title={item.status === 'active' ? 'Pausar' : 'Activar'}
                      className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {item.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                    </button>
                  </Can>

                  {/* Edit net proceeds */}
                  <Can cap="catalog.edit_price">
                    <button
                      onClick={() => startEditPrice(item)}
                      disabled={item.status === 'closed'}
                      title="Cambiar net proceeds"
                      className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <DollarSign size={14} />
                    </button>
                  </Can>

                  {/* Delete site item */}
                  <Can cap="catalog.close_delete">
                    <button
                      onClick={() => handleDeleteSiteItem(item)}
                      disabled={loadingAction === `delete-${item.site_item_id}` || item.status === 'deleted'}
                      title="Eliminar site item"
                      className="p-1.5 rounded-lg hover:bg-red-500/10 text-[var(--color-text-muted)] hover:text-red-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={14} />
                    </button>
                  </Can>

                  {/* External link */}
                  {item.permalink && (
                    <a
                      href={item.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Ver en marketplace"
                      className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
